import React, { Fragment } from "react";
import { ListBuilder, TileGrid, Form, useApiAction } from "@redriver/cinnamon";
import { PermissionCheck } from "components/auth";
import { HeaderBar, GroupSection } from "components/containers";
import { DocumentTypesDropdown } from "components/forms";
import { Actions, Targets } from "constants/Permissions";
import { addJobDocument, getJobDocuments, getJobSheets } from "./actions";
import { AddDocumentsModal } from "components/modals";
import TablePagination from "components/pagination/TablePagination";
import { Container } from "semantic-ui-react";
import { DateTime } from "luxon";
import { DocumentTypeIds } from "constants";
import JobDocumentTableActions from "./JobDocumentTableActions";

const JobDocumentsPanel = ({ jobId, fromEnquiry, departmentId }) => {
  const [jobSheetsRequest] = useApiAction(getJobSheets, {
    actionParams: jobId,
    lazy: false,
  });

  const jobSheetOptions = Array.isArray(jobSheetsRequest.response)
    ? jobSheetsRequest.response.map((x) => ({
        value: x.id,
        text: DateTime.fromISO(x.date).toFormat("DDDD"),
      }))
    : [];

  return (
    <ListBuilder
      withTiles
      loadAction={getJobDocuments}
      loadParams={{ jobId }}
      dataTransform={(data) => {
        const items = data.results || [];
        const byType = items.reduce((acc, x) => {
          const key = x.documentTypeName || "unknown";
          acc[key] = acc[key] || [];
          acc[key].push(x);
          return acc;
        }, {});
        const groups = Object.entries(byType).map(([documentType, items]) => ({
          documentType,
          items,
        }));
        return groups;
      }}
      initialPagination={{ pageSize: 25 }}
      totalTransform={(data) => data.totalResults}
      renderList={(tileProps, state, events) => {
        const { pageNumber, pageSize } = state.pagination;
        return (
          <Fragment>
            <Container className="documents-panel" fluid>
              <HeaderBar
                name="Documents"
                rightContent={
                  <PermissionCheck
                    target={Targets.ContractJob}
                    action={Actions.Edit}
                  >
                    <AddDocumentsModal
                      header="Add Job Document"
                      submitAction={addJobDocument}
                      submitParams={{ jobId }}
                      onAdded={events.onRefresh}
                      documentTypeDropdown={
                        <DocumentTypesDropdown
                          field="documentTypeId"
                          departmentId={departmentId}
                          label="Type"
                          placeholder="Select document type"
                          required
                          fluid
                        />
                      }
                    >
                      <Form.If
                        condition={({ fields }) =>
                          fields["documentTypeId"] ===
                          DocumentTypeIds.RiskAssessment
                        }
                      >
                        <Form.Group widths={2}>
                          <Form.Dropdown
                            field="jobSheetId"
                            label="Related Visit"
                            placeholder="Optionally select"
                            options={jobSheetOptions}
                            fluid
                            clearable
                          />
                        </Form.Group>
                      </Form.If>
                      <Form.Checkbox field="sendToApp" label="Send to app" />
                    </AddDocumentsModal>
                  </PermissionCheck>
                }
              />
              <TileGrid
                {...tileProps}
                dataKey="id"
                render={(item) => (
                  <GroupSection
                    group={item}
                    Id={jobId}
                    typeActions={(document) => (
                      <JobDocumentTableActions
                        document={{
                          ...document,
                          jobSheetId: document.jobSheetId,
                        }}
                        jobId={jobId}
                        departmentId={departmentId}
                        onRefresh={events.onRefresh}
                        jobSheetOptions={jobSheetOptions}
                      />
                    )}
                    onRefresh={events.onRefresh}
                    jobSheetOptions={jobSheetOptions}
                    fromEnquiry={fromEnquiry}
                  />
                )}
              />
            </Container>
            <TablePagination
              pageNumber={pageNumber}
              pageSize={pageSize}
              withPageSize
              pageSizeOptions={[25, 50, 100]}
              onChange={(changes) =>
                events.onChangePagination({
                  ...state.pagination,
                  ...changes,
                })
              }
              totalItems={state.total}
            />
          </Fragment>
        );
      }}
    />
  );
};

export default JobDocumentsPanel;
